import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { apiPost } from '../api/http';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { useAuth } from '../auth/AuthContext';
import PageHeader from '../components/PageHeader';
import {
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Divider,
} from '@mui/material';
import {
  handleMoneyShortcutKeyDown,
  parseINR,
  formatINR2dp,
  formatINR0dp,
  formatIndian2dp,
  formatIndianNumber
} from '../utils/MoneyShortcuts';

export default function LoanEligibility() {
  const { user } = useAuth();
  const isAdminUser = user?.role === 'admin' || user?.role === 'superadmin';
  const canUseSpeechToText = isAdminUser;

  // Read navigation params (after "Create Project" redirect)
  const { projectId, clientNameFromNav } = useMemo(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const pid = sp.get('projectId') || localStorage.getItem('lp.activeProjectId') || '';
      const cname = sp.get('clientName') || localStorage.getItem('lp.activeProjectClientName') || '';
      return { projectId: pid, clientNameFromNav: cname };
    } catch {
      return { projectId: '', clientNameFromNav: '' };
    }
  }, []);

  useEffect(() => {
    if (projectId) localStorage.setItem('lp.activeProjectId', String(projectId));
    if (clientNameFromNav) localStorage.setItem('lp.activeProjectClientName', String(clientNameFromNav));
  }, [projectId, clientNameFromNav]);

  if (!isAdminUser) {
    return (
      <div className="card">
        <h2>Loan Eligibility</h2>
        <p>This page is under development.</p>
      </div>
    );
  }

   const [form, setForm] = useState({
    // Client request: single Name field
    name: clientNameFromNav || '',
    age: '',
    email: '',
    phone: '',
    monthlyIncome: '',
    existingObligations: '',
    propertyValue: '',
    desiredLoan: '',
    averageMonthlyBalance: '',
    yearlyTurnover: '',
    sector: 'Trading',
    isSalaried: true,
    employerType: 'Pvt. Ltd.',
    tenureMonths: 240,
    annualInterestRatePct: 7.5,
    newObligation: ''
  });



  const [emiResult, setEmiResult] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);

  const [lastRunAt, setLastRunAt] = useState(null);

  // =========================
  // Speech-to-Text (Chrome)
  // =========================
  const sttSupported = useMemo(() => {
    if (!canUseSpeechToText) return false;
    if (typeof window === 'undefined') return false;
    return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  }, [canUseSpeechToText]);

  const [sttActiveField, setSttActiveField] = useState(null); // field name or null
  const [sttInterim, setSttInterim] = useState('');
  const [sttError, setSttError] = useState(null);
  const [smartVoiceEnabled] = useState(true);
  const [smartVoiceListening, setSmartVoiceListening] = useState(false);
  const [smartVoiceTranscript, setSmartVoiceTranscript] = useState('');

  const sttFocusedFieldRef = useRef(null);
  const sttRecRef = useRef(null);
  const resultTableScrollRef = useRef(null);
  const resultTableTopScrollRef = useRef(null);
  const smartVoiceRecRef = useRef(null);
  const smartVoiceLastTranscriptRef = useRef('');
  const smartVoiceShouldContinueRef = useRef(false);
  const smartVoiceRestartTimerRef = useRef(null);
  const smartVoiceSilenceTimerRef = useRef(null);
  const smartVoiceUnrelatedCountRef = useRef(0);

  const sttLastTranscriptRef = useRef('');
  const sttLastCtrlMsRef = useRef(0);
  const sttCtrlComboUsedRef = useRef(false);
  const sttLastAltMsRef = useRef(0);
  const sttAltComboUsedRef = useRef(false);
  const sttWantedFieldRef = useRef(null);
  const sttWantedActiveRef = useRef(false);

  function handleFocus(e) {
    const nm = e?.target?.name || e?.target?.getAttribute?.('name');
    if (nm) sttFocusedFieldRef.current = nm;
  }

  function tryExtractNumber(text) {
    const m = String(text || '').replace(/,/g, '').match(/-?\d+(?:\.\d+)?/);
    return m ? Number(m[0]) : NaN;
  }

  function parseSpokenInr(text) {
    const t = String(text || '').toLowerCase();

    // Common STT patterns: "3 crore", "3 cr", "3 c", "2.5 lakh", "50 thousand", "40 k"
    const n = tryExtractNumber(t);
    if (!Number.isFinite(n)) return null;

    let mult = 1;
    if (/(crore|cr\b)/.test(t) || /\b\d+\s*c\b/.test(t)) mult = 1e7;
    else if (/(lakh|lac\b|lacs\b)/.test(t) || /\b\d+\s*l\b/.test(t)) mult = 1e5;
    else if (/(thousand|k\b)/.test(t)) mult = 1e3;
    else if (/(million|mn\b)/.test(t)) mult = 1e6;

    const value = Math.round(n * mult);
    return value;
  }


  function normalizeSpokenValue(field, rawText) {
    const raw = String(rawText || '').trim();
    if (!raw) return '';

    const f = String(field || '').toLowerCase();

    if (f === 'email') {
      return raw
        .toLowerCase()
        .replace(/\s+at\s+/g, '@')
        .replace(/\s+dot\s+/g, '.')
        .replace(/\s+/g, '');
    }

    if (f === 'phone') {
      return raw.replace(/\D+/g, '');
    }

    if (f === 'age' || f === 'tenuremonths') {
      const n = tryExtractNumber(raw);
      return Number.isFinite(n) ? String(Math.max(0, Math.round(n))) : raw;
    }

    if (f === 'annualinterestratepct') {
      const n = tryExtractNumber(raw);
      return Number.isFinite(n) ? String(n) : raw;
    }

    if (f === 'issalaried') {
      const t = raw.toLowerCase();
      if (t.includes('salary') || t.includes('salaried')) return true;
      if (t.includes('self employed') || t.includes('self-employed') || t.includes('business')) return false;
      return raw;
    }

    if (f === 'sector') {
      const t = raw.toLowerCase();
      if (t.includes('service')) return 'Service';
      if (t.includes('manufacturing')) return 'Manufacturing';
      if (t.includes('trading')) return 'Trading';
      if (t.includes('distributor')) return 'Distributor';
      return 'Other';
    }

    const moneyFields = new Set([
      'monthlyincome',
      'existingobligations',
      'propertyvalue',
      'desiredloan',
      'averagemonthlybalance',
      'yearlyturnover',
      'newobligation',
    ]);

    if (moneyFields.has(f)) {
      const parsed = parseSpokenInr(raw);
      if (parsed != null) return formatIndianNumber(parsed);

      const n = tryExtractNumber(raw);
      return Number.isFinite(n) ? formatIndianNumber(Math.round(n)) : raw;
    }

    return raw;
  }


  function stopRecognition({ keepWanted = false } = {}) {
    try {
      if (sttRecRef.current) sttRecRef.current.stop();
    } catch { }
    sttRecRef.current = null;
    setSttInterim('');
    if (!keepWanted) {
      sttWantedActiveRef.current = false;
      sttWantedFieldRef.current = null;
      setSttActiveField(null);
    }
  }

  function stopStt() {
    stopRecognition({ keepWanted: false });
  }

  function suspendSttIfRunning() {
    // Stop the microphone while the tab/window isn't active, but keep the user's "wanted" state
    // so we can resume automatically when focus returns.
    if (!sttWantedActiveRef.current || !sttWantedFieldRef.current) return;
    stopRecognition({ keepWanted: true });
    // Keep the UI showing the intended active field.
    setSttActiveField(sttWantedFieldRef.current);
  }


  function startSttForField(fieldName) {
    if (!sttSupported) return;

    // Mark intent so we can auto-resume after tab/app focus changes.
    sttWantedFieldRef.current = fieldName;
    sttWantedActiveRef.current = true;

    // Stop any previous recognition instance but keep "wanted" state.
    stopRecognition({ keepWanted: true });

    setSttError(null);
    setSttInterim('');
    setSttActiveField(fieldName);
    sttLastTranscriptRef.current = '';

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    sttRecRef.current = rec;

    rec.lang = 'en-IN';
    rec.continuous = false;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onresult = (e) => {
      let interim = '';
      let finalText = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0]?.transcript || '';
        if (e.results[i].isFinal) finalText += t;
        else interim += t;
      }
      if (interim) {
        const it = interim.trim();
        setSttInterim(it);
        sttLastTranscriptRef.current = it;
      }

      if (finalText) {
        const normalized = normalizeSpokenValue(fieldName, finalText);
        setForm((f) => ({ ...f, [fieldName]: normalized }));
        setSttInterim('');
        sttLastTranscriptRef.current = '';
        // Stop automatically after final
        stopStt();
      }
    };

    rec.onerror = (e) => {
      // Common error codes: 'not-allowed', 'service-not-allowed', 'audio-capture', 'no-speech', 'aborted'
      const code = e?.error || 'speech_error';
      setSttError(code);
      stopStt();
    };

    rec.onend = () => {
      // Some Chrome builds end STT when the tab loses focus / becomes hidden.
      // We treat that as a "pause" (so we can resume on focus/visibility return),
      // unless the user explicitly stopped STT.
      const pending = String(sttLastTranscriptRef.current || '').trim();
      if (pending && sttActiveField === fieldName) {
        const normalized = normalizeSpokenValue(fieldName, pending);
        setForm((f) => ({ ...f, [fieldName]: normalized }));
      }
      sttLastTranscriptRef.current = '';
      sttRecRef.current = null;
      setSttInterim('');

      const wantedSameField =
        sttWantedActiveRef.current && sttWantedFieldRef.current === fieldName;

      // If the user still "wants" STT for this field but the page isn't active,
      // keep the UI state and let the focus/visibility handler restart the mic.
      if (wantedSameField && (document.hidden || !document.hasFocus())) {
        setSttActiveField(fieldName);
        return;
      }

      // Otherwise, clear active state as before.
      setSttActiveField((cur) => (cur === fieldName ? null : cur));
    };

    try {
      rec.start();
    } catch (err) {
      setSttError('speech_start_failed');
      stopStt();
    }
  }

  function toggleSttForField(fieldName) {
    if (!fieldName) return;
    if (sttActiveField === fieldName) stopStt();
    else startSttForField(fieldName);
  }

  function toggleSttForFocusedField() {
    const fieldName = sttFocusedFieldRef.current;
    if (!fieldName) return;
    toggleSttForField(fieldName);
  }

  const SMART_VOICE_FIELD_PATTERNS = [
    { field: 'monthlyIncome', patterns: ['monthly income', 'income per month', 'salary', 'income'] },
    { field: 'existingObligations', patterns: ['existing obligations', 'existing emi', 'existing obligation', 'obligations'] },
    { field: 'propertyValue', patterns: ['property value', 'property amount', 'value of property'] },
    { field: 'desiredLoan', patterns: ['desired loan', 'loan amount required', 'required loan', 'loan required'] },
    { field: 'averageMonthlyBalance', patterns: ['average monthly balance', 'average bank balance', 'avg monthly balance', 'avg bank balance', 'abb', 'bank balance average'] },
    { field: 'yearlyTurnover', patterns: ['yearly turnover', 'annual turnover', 'gross turnover', 'turnover'] },
    { field: 'sector', patterns: ['sector', 'industry', 'distributor'] },
    { field: 'name', patterns: ['name', 'client name', 'customer name'] },
    { field: 'age', patterns: ['age'] },
    { field: 'email', patterns: ['email', 'email address'] },
    { field: 'phone', patterns: ['phone', 'mobile', 'phone number', 'mobile number'] },
    { field: 'tenureMonths', patterns: ['tenure months', 'loan tenure', 'tenure'] },
    { field: 'annualInterestRatePct', patterns: ['interest rate', 'rate of interest'] },
    { field: 'newObligation', patterns: ['new obligation', 'new emi', 'emi'] },
    { field: 'isSalaried', patterns: ['occupation', 'employment type', 'applicant type'] }
  ];

  function extractSmartVoiceFieldAndValue(transcript) {
    const text = String(transcript || '').trim().toLowerCase();
    if (!text) return null;

    let best = null;

    for (const item of SMART_VOICE_FIELD_PATTERNS) {
      for (const pattern of item.patterns) {
        const idx = text.indexOf(pattern);
        if (idx === -1) continue;

        const end = idx + pattern.length;
        const rawTail = text.slice(end).replace(/^\s*(is|as|equals|equal to|:|-)\s*/i, '').trim();

        if (!best || idx < best.index || (idx === best.index && pattern.length > best.pattern.length)) {
          best = {
            field: item.field,
            pattern,
            index: idx,
            rawValue: rawTail
          };
        }
      }
    }

    return best;
  }

  function applySmartVoiceTranscript(transcript) {
    const match = extractSmartVoiceFieldAndValue(transcript);
    if (!match || !match.field) return false;

    const normalized = normalizeSpokenValue(match.field, match.rawValue);

    if (match.field === 'isSalaried') {
      if (normalized === true || normalized === false) {
        setForm((f) => ({ ...f, isSalaried: normalized }));
        return true;
      }
      return false;
    }

    if (match.field === 'sector') {
      setForm((f) => ({ ...f, sector: String(normalized || 'Other') }));
      return true;
    }

    setForm((f) => ({ ...f, [match.field]: normalized }));
    return true;
  }

  const SMART_VOICE_SILENCE_TIMEOUT_MS = 15000;
  const SMART_VOICE_UNRELATED_LIMIT = 2;

  function resetSmartVoiceSilenceTimer() {
    if (smartVoiceSilenceTimerRef.current) {
      clearTimeout(smartVoiceSilenceTimerRef.current);
    }

    smartVoiceSilenceTimerRef.current = setTimeout(() => {
      setSttError('Smart Voice auto-stopped after 15 seconds of silence.');
      stopSmartVoiceCapture();
    }, SMART_VOICE_SILENCE_TIMEOUT_MS);
  }

  function clearSmartVoiceSilenceTimer() {
    if (smartVoiceSilenceTimerRef.current) {
      clearTimeout(smartVoiceSilenceTimerRef.current);
      smartVoiceSilenceTimerRef.current = null;
    }
  }

  function stopSmartVoiceCapture() {
    smartVoiceShouldContinueRef.current = false;

    if (smartVoiceRestartTimerRef.current) {
      clearTimeout(smartVoiceRestartTimerRef.current);
      smartVoiceRestartTimerRef.current = null;
    }

    clearSmartVoiceSilenceTimer();
    smartVoiceUnrelatedCountRef.current = 0;

    try {
      if (smartVoiceRecRef.current) smartVoiceRecRef.current.stop();
    } catch { }

    smartVoiceRecRef.current = null;
    smartVoiceLastTranscriptRef.current = '';
    setSmartVoiceListening(false);
  }


    function startSmartVoiceCapture() {
    if (!sttSupported) return;

    stopStt();

    if (smartVoiceRestartTimerRef.current) {
      clearTimeout(smartVoiceRestartTimerRef.current);
      smartVoiceRestartTimerRef.current = null;
    }

    clearSmartVoiceSilenceTimer();
    smartVoiceUnrelatedCountRef.current = 0;

    try {
      if (smartVoiceRecRef.current) {
        smartVoiceRecRef.current.stop();
      }
    } catch {}

    setSttError(null);
    setSmartVoiceTranscript('');
    smartVoiceShouldContinueRef.current = true;
    smartVoiceUnrelatedCountRef.current = 0;

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    smartVoiceRecRef.current = rec;

    rec.lang = 'en-IN';
    rec.continuous = false;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onstart = () => {
      setSmartVoiceListening(true);
      resetSmartVoiceSilenceTimer();
    };

    rec.onresult = (e) => {
      let interim = '';
      let finalText = '';

      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0]?.transcript || '';
        if (e.results[i].isFinal) finalText += t;
        else interim += t;
      }

      if (interim) {
        const trimmedInterim = interim.trim();
        smartVoiceLastTranscriptRef.current = trimmedInterim;
        setSmartVoiceTranscript(trimmedInterim);
        resetSmartVoiceSilenceTimer();
      }

      if (finalText) {
        const finalTrimmed = finalText.trim();
        smartVoiceLastTranscriptRef.current = finalTrimmed;
        setSmartVoiceTranscript(finalTrimmed);
        resetSmartVoiceSilenceTimer();

        const applied = applySmartVoiceTranscript(finalTrimmed);
        if (applied) {
          smartVoiceUnrelatedCountRef.current = 0;
        } else {
          smartVoiceUnrelatedCountRef.current += 1;
          if (smartVoiceUnrelatedCountRef.current >= SMART_VOICE_UNRELATED_LIMIT) {
            setSttError('Smart Voice auto-stopped because the speech did not match the form.');
            stopSmartVoiceCapture();
          }
        }
      }
    };

    rec.onerror = (e) => {
      const errorCode = e?.error || 'speech_error';

      if (errorCode === 'no-speech' || errorCode === 'aborted') {
        return;
      }

      setSttError(errorCode);
      smartVoiceShouldContinueRef.current = false;
      smartVoiceRecRef.current = null;
      setSmartVoiceListening(false);
    };

    rec.onend = () => {
      const pending = String(smartVoiceLastTranscriptRef.current || '').trim();
      if (pending) {
        const applied = applySmartVoiceTranscript(pending);
        if (applied) smartVoiceUnrelatedCountRef.current = 0;
      }

      smartVoiceRecRef.current = null;
      setSmartVoiceListening(false);

      if (smartVoiceShouldContinueRef.current && smartVoiceEnabled) {
        smartVoiceRestartTimerRef.current = setTimeout(() => {
          startSmartVoiceCapture();
        }, 250);
      }
    };

    try {
      rec.start();
    } catch {
      setSttError('speech_start_failed');
      smartVoiceShouldContinueRef.current = false;
      stopSmartVoiceCapture();
    }
  }

  

  // Keyboard shortcuts:
  // 1) Ctrl + Shift + S  -> toggle STT for focused field
  // 2) Double Ctrl       -> toggle STT for focused field (fast)
  // 3) Esc               -> stop STT
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === 'Escape') {
        if (sttActiveField) {
          e.preventDefault();
          stopStt();
        }
        return;
      }

      // Ctrl + Shift + S
      if (e.ctrlKey && e.shiftKey && (e.code === 'KeyS' || e.key?.toLowerCase() === 's')) {
        e.preventDefault();
        toggleSttForFocusedField();
        return;
      }

      // Double Ctrl (only if Ctrl wasn't used in a combo in-between)
      if (e.key === 'Control') {
        if (e.repeat) return; // ignore key-repeat when holding Ctrl
        const now = Date.now();
        const last = sttLastCtrlMsRef.current || 0;
        if (last && now - last <= 350 && !sttCtrlComboUsedRef.current) {
          e.preventDefault();
          sttLastCtrlMsRef.current = 0;
          sttCtrlComboUsedRef.current = false;
          toggleSttForFocusedField();
        } else {
          sttLastCtrlMsRef.current = now;
          sttCtrlComboUsedRef.current = false;
        }
        return;
      }

      // Double Alt (Chrome uses single-Alt to focus the browser menu; preventDefault keeps key events reliable)
      if (e.key === 'Alt') {
        if (e.repeat) return; // ignore key-repeat when holding Alt
        // Prevent Chrome/Windows menu focus stealing our second Alt press (only when Alt is pressed alone)
        if (!e.ctrlKey && !e.shiftKey && !e.metaKey) {
          e.preventDefault();
          e.stopPropagation?.();
        }

        const now = Date.now();
        const last = sttLastAltMsRef.current || 0;
        if (last && now - last <= 350 && !sttAltComboUsedRef.current) {
          sttLastAltMsRef.current = 0;
          sttAltComboUsedRef.current = false;
          toggleSttForFocusedField();
        } else {
          sttLastAltMsRef.current = now;
          sttAltComboUsedRef.current = false;
        }
        return;
      }

      // If user pressed another key while holding Ctrl, mark as combo-used to avoid accidental double-ctrl toggle
      if (e.ctrlKey) sttCtrlComboUsedRef.current = true;
      if (e.altKey) sttAltComboUsedRef.current = true;
    }

    window.addEventListener('keydown', onKeyDown, { passive: false });
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [sttActiveField, sttSupported]);


  // Auto-resume STT when the user switches away and comes back (Chrome often stops recognition).
  useEffect(() => {
    function tryResume() {
      if (!sttSupported) return;
      const wanted = sttWantedActiveRef.current ? sttWantedFieldRef.current : null;
      if (!wanted) return;

      // Only resume when the page is actually active.
      if (document.hidden) return;
      if (!document.hasFocus()) return;

      // If a recognizer is already running, do nothing.
      if (sttRecRef.current) return;

      // Restart mic for the wanted field.
      startSttForField(wanted);
    }

    function onVisibility() {
      if (document.hidden) {
        suspendSttIfRunning();
      } else {
        // When tab becomes visible again, attempt resume.
        tryResume();
      }
    }

    function onBlur() {
      suspendSttIfRunning();
    }

    function onFocus() {
      tryResume();
    }

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
      stopSmartVoiceCapture();
    };


  }, [sttSupported]);


  function renderFieldLabel(label, unit = '') {
    return (
      <span className="loan-field-label">
        <span>{label}</span>
        {unit ? <span className="loan-field-unit">{unit}</span> : null}
      </span>
    );
  }

  function formatPercent(value) {
    const n = Number(value);
    return Number.isFinite(n) ? `${n.toFixed(1)}%` : '—';
  }

  function getDisplayProductName(product) {
    const raw = String(product || '').trim();
    if (/^amb$/i.test(raw)) return 'ABB';
    if (/^gross\s*turn\s*over$/i.test(raw)) return 'Gross Turnover';
    if (/^foir$/i.test(raw)) return 'Vanilla';
    if (/^vanilla$/i.test(raw)) return 'Vanilla';
    if (/^double\s*whammy$/i.test(raw)) return 'Double Whammy';
    if (/^low\s*ltv$/i.test(raw)) return 'Low LTV';
    return raw || 'Product';
  }

  function getResultSecondaryText(result) {
    const meta = result?.meta || {};
    const parts = [];

    if (Number.isFinite(Number(meta.recommendedFoirPct)) && Number(meta.allowedFoirPct) > 0) {
      parts.push(`FOIR ${formatPercent(meta.recommendedFoirPct)} / ${formatPercent(meta.allowedFoirPct)}`);
    }

    if (Number.isFinite(Number(meta.recommendedLtvPct)) && Number(meta.allowedLtvPct) > 0) {
      parts.push(`LTV ${formatPercent(meta.recommendedLtvPct)} / ${formatPercent(meta.allowedLtvPct)}`);
    }

    if (Number.isFinite(Number(meta.combinedPct)) && Number(meta.allowedCombinedPct) > 0) {
      parts.push(`Combined ${formatPercent(meta.combinedPct)} / ${formatPercent(meta.allowedCombinedPct)}`);
    }

    if (Number.isFinite(Number(meta.allowedAbbEmiPct)) && Number(meta.allowedAbbEmiPct) > 0) {
      parts.push(`ABB basis ${formatPercent(meta.allowedAbbEmiPct)}`);
    }

    if (Number.isFinite(Number(meta.turnoverFactorPct)) && meta.sector) {
      parts.push(`${meta.sector} ${formatPercent(meta.turnoverFactorPct)}`);
    }

    if (Number.isFinite(Number(meta.rate)) && Number(meta.rate) > 0) {
      parts.push(`ROI ${formatPercent(meta.rate)}`);
    }

    if (meta.loanCapEnforced && Number.isFinite(Number(meta.maxLoanCap)) && Number(meta.maxLoanCap) > 0) {
      parts.push(`Cap ${formatINR0dp(Number(meta.maxLoanCap))}`);
    }

    return parts.join(' • ');
  }

  function getResultDecisionText(result) {
    const meta = result?.meta || {};
    const reason = String(meta.reason || '').trim();
    const desiredLoan = Number(meta.desiredLoan || 0);
    const shortfallAmount = Number(meta.shortfallAmount || 0);
    const loanAmount = Number(result?.loanAmount || 0);

    if (reason) {
      if (!result?.isApproved && loanAmount > 0 && desiredLoan > 0 && shortfallAmount > 0) {
        return `${reason} Allowable ${formatINR2dp(loanAmount)} vs requested ${formatINR2dp(desiredLoan)}.`;
      }
      return reason;
    }

    if (!result?.isApproved && loanAmount > 0 && desiredLoan > 0) {
      return `Allowable ${formatINR2dp(loanAmount)} vs requested ${formatINR2dp(desiredLoan)}.`;
    }

    return '';
  }

  function buildInputChecks(resultRows) {
    const checks = [];
    const monthlyIncome = parseINR(form.monthlyIncome);
    const existingObligations = parseINR(form.existingObligations);
    const propertyValue = parseINR(form.propertyValue);
    const desiredLoan = parseINR(form.desiredLoan);
    const averageMonthlyBalance = parseINR(form.averageMonthlyBalance);
    const yearlyTurnover = parseINR(form.yearlyTurnover);

    if (Number.isFinite(monthlyIncome) && Number.isFinite(existingObligations) && monthlyIncome > 0) {
      const ratio = (existingObligations / monthlyIncome) * 100;
      checks.push({
        severity: ratio >= 100 ? 'warning' : 'info',
        text: `Existing obligations are ${ratio.toFixed(1)}% of monthly income.`
      });
    }

    if (Number.isFinite(desiredLoan) && Number.isFinite(propertyValue) && propertyValue > 0) {
      const desiredLtv = (desiredLoan / propertyValue) * 100;
      checks.push({
        severity: desiredLtv > 80 ? 'warning' : 'info',
        text: `Requested loan is ${desiredLtv.toFixed(1)}% of property value.`
      });
    }

    if (!form.isSalaried && (!Number.isFinite(yearlyTurnover) || yearlyTurnover <= 0)) {
      checks.push({ severity: 'warning', text: 'Gross Turnover products need yearly turnover for self-employed applicants.' });
    }

    if (Number.isFinite(averageMonthlyBalance) && averageMonthlyBalance > 0 && Number.isFinite(desiredLoan) && desiredLoan > 0) {
      checks.push({
        severity: 'info',
        text: `ABB basis entered: ${formatINR2dp(averageMonthlyBalance)} average monthly balance.`
      });
    }

    const rejectedWithAllowance = (Array.isArray(resultRows) ? resultRows : []).filter((x) => !x?.isApproved && Number(x?.loanAmount || 0) > 0).length;
    if (rejectedWithAllowance > 0) {
      checks.push({
        severity: 'info',
        text: `${rejectedWithAllowance} product(s) are below the requested amount but still have a non-zero allowable value.`
      });
    }

    return checks;
  }

  function updateForm(e) {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  }

  function setMoneyField(fieldName, value) {
    setForm((f) => ({ ...f, [fieldName]: value }));
  }

  function formatMoneyOnBlur(fieldName, { decimals = 0 } = {}) {
    // Indian comma auto-format on blur.
    // decimals=0 for most rupee fields, decimals=2 for EMI/newObligation.
    const raw = form[fieldName];

    if (raw === null || raw === undefined || String(raw).trim() === '') {
      setMoneyField(fieldName, '');
      return;
    }

    const n = parseINR(raw);
    if (!Number.isFinite(n)) return; // don't rewrite invalid input

    const formatted = decimals === 2
      ? formatIndian2dp(n)
      : formatIndianNumber(n, { maxFractionDigits: 0 });

    setMoneyField(fieldName, formatted);
  }


  async function calculateEmi() {
    try {
      const res = await apiPost('/api/loan/calc', {
        loanAmount: parseINR(form.desiredLoan),
        annualRatePct: Number(form.annualInterestRatePct),
        tenureMonths: Number(form.tenureMonths)
      });

      const emi = Number(res?.monthlyEmi || 0);
      setEmiResult(emi);
      setLastRunAt(new Date());

      // Keep it input-friendly (commas + 2 decimals, no ₹)
      setForm((f) => ({ ...f, newObligation: formatIndian2dp(emi) }));
    } catch {
      alert('Failed to calculate EMI');
    }
  }

  async function runOptimization() {
    setLoading(true);
    try {
      const res = await apiPost('/api/loan/optimize', {
        request: {
          ...form,
          industry: form.sector,
          propertyType: form.propertyType,

          // Backward compatibility until DB/schema + API are updated:
          // keep sending legacy fields so backend doesn't break.
          firstName: String(form.name || ''),
          middleName: '',
          lastName: '',

          monthlyIncome: parseINR(form.monthlyIncome),
          existingObligations: parseINR(form.existingObligations),
          propertyValue: parseINR(form.propertyValue),
          desiredLoan: parseINR(form.desiredLoan),
          averageMonthlyBalance: parseINR(form.averageMonthlyBalance),
          yearlyTurnover: parseINR(form.yearlyTurnover),
          tenureMonths: Number(form.tenureMonths),
          annualInterestRatePct: Number(form.annualInterestRatePct),
          newObligation: 0
        }
      });
      setResults(res);
    } catch {
      alert('Failed to run loan optimization');
    } finally {
      setLoading(false);
      setLastRunAt(new Date());
    }
  }

  const bestOverall = results?.bestOverall || null;
  const allResultRows = Array.isArray(results?.allResults) ? results.allResults : [];
  const approvedCount = allResultRows.filter((x) => x?.isApproved).length;
  const rejectedCount = allResultRows.filter((x) => !x?.isApproved).length;
  const inputChecks = useMemo(() => buildInputChecks(allResultRows), [results, form]);

  const suggestedTenure = useMemo(() => {
    const ageNum = Number(form.age);
    if (!Number.isFinite(ageNum) || ageNum <= 0) return null;
    const maxAge = form.isSalaried ? 60 : 70;
    const years = Math.max(0, Math.floor(maxAge - ageNum));
    const months = years * 12;
    return { years, months, maxAge, ageNum };
  }, [form.age, form.isSalaried]);


  // Pivot table: 1 row per bank, products in columns, NA where missing
  const bankProductMatrix = useMemo(() => {
    const rows = results?.allResults ? [...results.allResults] : [];

    const preferredOrder = ['Vanilla', 'Double Whammy', 'ABB', 'Gross Turnover', 'Low LTV'];

    const banksSet = new Set();
    const productsSet = new Set();
    const matrix = {};

    for (const r of rows) {
      const bank = String(r.bankName || '').trim();
      let product = String(r.product || '').trim();
      // Client naming + grouping
      if (/^amb$/i.test(product)) product = 'ABB';
      if (/^gross\s*turn\s*over$/i.test(product)) product = 'Gross Turnover';
      if (/^foir$/i.test(product)) product = 'Vanilla';
      if (/^system$/i.test(product)) product = ''; // hide System
      if (/^vanilla$/i.test(product)) product = 'Vanilla';
      if (/^double\s*whammy$/i.test(product)) product = 'Double Whammy';
      if (/^low\s*ltv$/i.test(product)) product = 'Low LTV';
      if (!bank || !product) continue;

      banksSet.add(bank);
      productsSet.add(product);

      if (!matrix[bank]) matrix[bank] = {};
      const prev = matrix[bank][product];
      const prevAmount = Number(prev?.loanAmount || 0);
      const nextAmount = Number(r.loanAmount || 0);
      if (!prev || nextAmount > prevAmount) {
        matrix[bank][product] = r;
      }
    }

    const banks = Array.from(banksSet).sort((a, b) => a.localeCompare(b));

    const preferredProducts = preferredOrder.filter((p) => productsSet.has(p));
    const extraProducts = Array.from(productsSet).filter((p) => p !== 'LTV' && !preferredOrder.includes(p)).sort((a, b) => a.localeCompare(b));
    const products = [...preferredProducts, ...extraProducts];

    return { banks, products, matrix };
  }, [results]);

  const tableMinWidth = useMemo(() => {
    return Math.max(1280, 200 + (bankProductMatrix.products.length * 280));
  }, [bankProductMatrix.products.length]);

  useEffect(() => {
    if (!Array.isArray(allResultRows) || allResultRows.length === 0) {
      setSelectedOffer(null);
      return;
    }

    setSelectedOffer((current) => {
      if (current) {
        const exactMatch = allResultRows.find((item) =>
          String(item?.bankName || '') === String(current?.bankName || '') &&
          String(item?.product || '') === String(current?.product || ''));
        if (exactMatch) return exactMatch;
      }

      return bestOverall || allResultRows[0] || null;
    });
  }, [allResultRows, bestOverall]);

  function formatPdfCurrency(value) {
    const numeric = Number(value || 0);
    if (!Number.isFinite(numeric)) return 'INR 0.00';
    return String(formatINR2dp(numeric)).replace(/₹/g, 'INR ');
  }

  function escapePdfText(value) {
    return String(value ?? '')
      .replace(/\\/g, '\\\\')
      .replace(/\(/g, '\\(')
      .replace(/\)/g, '\\)')
      .replace(/\r/g, ' ')
      .replace(/\n/g, ' ');
  }

  function pdfWrapText(textValue, maxChars) {
    const normalized = String(textValue || '').replace(/\s+/g, ' ').trim();
    if (!normalized) return ['—'];

    const words = normalized.split(' ');
    const lines = [];
    let current = '';

    for (const word of words) {
      const candidate = current ? `${current} ${word}` : word;
      if (candidate.length <= maxChars) {
        current = candidate;
        continue;
      }

      if (current) {
        lines.push(current);
        current = '';
      }

      if (word.length <= maxChars) {
        current = word;
        continue;
      }

      let remaining = word;
      while (remaining.length > maxChars) {
        lines.push(`${remaining.slice(0, Math.max(1, maxChars - 1))}-`);
        remaining = remaining.slice(Math.max(1, maxChars - 1));
      }
      current = remaining;
    }

    if (current) lines.push(current);
    return lines.length ? lines : ['—'];
  }

  function buildPdfBlob(streamContent) {
    const encoder = new TextEncoder();
    const getByteLength = (value) => encoder.encode(value).length;
    const objects = [];
    const pushObject = (body) => {
      objects.push(body);
      return objects.length;
    };

    const catalogId = pushObject('<< /Type /Catalog /Pages 2 0 R >>');
    pushObject('<< /Type /Pages /Kids [3 0 R] /Count 1 >>');
    pushObject('<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 4 0 R /F2 5 0 R >> >> /Contents 6 0 R >>');
    pushObject('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>');
    pushObject('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>');
    pushObject(`<< /Length ${getByteLength(streamContent)} >>\nstream\n${streamContent}\nendstream`);

    let pdf = '%PDF-1.4\n';
    const offsets = [0];

    for (let index = 0; index < objects.length; index += 1) {
      offsets.push(getByteLength(pdf));
      pdf += `${index + 1} 0 obj\n${objects[index]}\nendobj\n`;
    }

    const xrefStart = getByteLength(pdf);
    pdf += `xref\n0 ${objects.length + 1}\n`;
    pdf += '0000000000 65535 f \n';

    for (let index = 1; index < offsets.length; index += 1) {
      pdf += `${String(offsets[index]).padStart(10, '0')} 00000 n \n`;
    }

    pdf += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefStart}\n%%EOF`;
    return new Blob([pdf], { type: 'application/pdf' });
  }

  function buildLoanSummaryPdfBlob(offer) {
    const pageWidth = 595;
    const pageHeight = 842;
    const marginLeft = 42.52;
    const marginRight = 42.52;
    const marginTop = 70.86;
    const marginBottom = 70.86;

    const commands = [];
    const write = (command) => commands.push(command);
    const textLine = (fontName, fontSize, x, y, textValue) => {
      write(`BT /${fontName} ${fontSize} Tf 1 0 0 1 ${x.toFixed(2)} ${y.toFixed(2)} Tm (${escapePdfText(textValue)}) Tj ET\n`);
    };
    const horizontalRule = (x1, y1, x2, width = 1) => {
      write(`${width.toFixed(2)} w ${x1.toFixed(2)} ${y1.toFixed(2)} m ${x2.toFixed(2)} ${y1.toFixed(2)} l S\n`);
    };
    const outlineRect = (x, y, width, height, lineWidth = 0.9) => {
      write(`${lineWidth.toFixed(2)} w ${x.toFixed(2)} ${y.toFixed(2)} ${width.toFixed(2)} ${height.toFixed(2)} re S\n`);
    };

    const contentWidth = pageWidth - marginLeft - marginRight;
    const meta = offer?.meta || {};
    const decisionLabel = offer?.isApproved ? 'Eligible' : 'Not Eligible';
    const titleText = `${offer?.bankName || 'Bank'} - ${getDisplayProductName(offer?.product)}`;
    const amountText = formatPdfCurrency(offer?.loanAmount || 0);

    let y = pageHeight - marginTop;
    textLine('F2', 24, marginLeft, y, 'JetFin');
    textLine('F2', 9, pageWidth - marginRight - 72, y + 1, 'LOAN SUMMARY');
    y -= 28;

    textLine('F2', 15, marginLeft, y, titleText);
    textLine('F2', 18, pageWidth - marginRight - 155, y, amountText);
    y -= 16;
    textLine('F1', 10, marginLeft, y, decisionLabel);
    textLine('F1', 10, pageWidth - marginRight - 124, y, 'Allowable loan amount');
    y -= 14;
    horizontalRule(marginLeft, y, pageWidth - marginRight, 1.2);
    y -= 22;

    const applicantFields = [
      ['Applicant name', form.name || '—'],
      ['Email', form.email || '—'],
      ['Phone', form.phone || '—'],
      ['Age', form.age || '—'],
      ['Occupation', form.isSalaried ? 'Salaried' : 'Self Employed'],
      ['Employer / Sector', form.isSalaried ? (form.employerType || '—') : (form.sector || '—')],
    ];

    const loanFields = [
      ['Monthly income', formatPdfCurrency(parseINR(form.monthlyIncome))],
      ['Existing obligations', formatPdfCurrency(parseINR(form.existingObligations))],
      ['Property value', formatPdfCurrency(parseINR(form.propertyValue))],
      ['Desired loan', formatPdfCurrency(parseINR(form.desiredLoan))],
      ['ABB', formatPdfCurrency(parseINR(form.averageMonthlyBalance))],
      ['EMI', formatPdfCurrency(parseINR(form.newObligation))],
      ['Tenure entered', `${Number(form.tenureMonths || 0)} months`],
      ['Interest rate used', `${Number(form.annualInterestRatePct || 0).toFixed(1)}% p.a.`],
      ['Yearly turnover', form.isSalaried ? '—' : formatPdfCurrency(parseINR(form.yearlyTurnover))],
    ];

    const productFields = [
      ['Bank', offer?.bankName || '—'],
      ['Product', getDisplayProductName(offer?.product) || '—'],
      ['Decision', decisionLabel],
      ['Allowable amount', amountText],
      ['Rate used', Number(meta.rate) > 0 ? `${Number(meta.rate).toFixed(1)}%` : '—'],
      ['Optimizer', offer?.optimizer || '—'],
      ['Secondary', getResultSecondaryText(offer) || '—'],
      ['Decision note', getResultDecisionText(offer) || '—'],
    ];

    const drawSection = (title, entries, leftX, topY, width, maxChars) => {
      const titleY = topY;
      textLine('F2', 12, leftX, titleY, title);
      let currentY = titleY - 16;

      for (const [label, value] of entries) {
        textLine('F2', 9, leftX, currentY, `${label}:`);
        const wrappedLines = pdfWrapText(value, maxChars);
        let valueY = currentY;

        for (const wrappedLine of wrappedLines) {
          textLine('F1', 9, leftX + 104, valueY, wrappedLine);
          valueY -= 12;
        }

        currentY = valueY - 2;
      }

      const bottomY = currentY - 6;
      outlineRect(leftX - 10, bottomY, width, (titleY - bottomY) + 14, 0.8);
      return bottomY - 16;
    };

    const leftWidth = 238;
    const rightWidth = 238;
    const gap = 24;
    const firstTop = y;
    const leftBottom = drawSection('Applicant details', applicantFields, marginLeft, firstTop, leftWidth, 24);
    const rightBottom = drawSection('Entered loan inputs', loanFields, marginLeft + leftWidth + gap, firstTop, rightWidth, 18);

    y = Math.min(leftBottom, rightBottom);
    y = drawSection('Selected loan product', productFields, marginLeft, y, contentWidth, 47);

    const footerText = `Generated on ${new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date())}`;
    textLine('F1', 9, marginLeft, marginBottom - 16, footerText);
    textLine('F1', 9, pageWidth - marginRight - 108, marginBottom - 16, 'JetFin LoanProgress');

    return buildPdfBlob(commands.join(''));
  }

  function generateOfferPdf(offer) {
    if (!offer) return;

    const fileName = `JetFin_${String(offer?.bankName || 'Bank').replace(/[^a-z0-9]+/gi, '_')}_${String(getDisplayProductName(offer?.product) || 'Product').replace(/[^a-z0-9]+/gi, '_')}.pdf`;
    const blob = buildLoanSummaryPdfBlob(offer);
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1500);
  }

  useLayoutEffect(() => {
    const tableWrap = resultTableScrollRef.current;
    const topWrap = resultTableTopScrollRef.current;
    if (!tableWrap || !topWrap) return undefined;

    const syncTopMetrics = () => {
      const track = topWrap.firstElementChild;
      if (track) {
        track.style.width = `${tableWrap.scrollWidth}px`;
        track.style.height = '1px';
      }
      topWrap.style.display = tableWrap.scrollWidth > tableWrap.clientWidth + 2 ? 'block' : 'none';
      topWrap.scrollLeft = tableWrap.scrollLeft;
    };

    let syncingFromTop = false;
    let syncingFromTable = false;

    const handleTopScroll = () => {
      if (syncingFromTable) return;
      syncingFromTop = true;
      tableWrap.scrollLeft = topWrap.scrollLeft;
      syncingFromTop = false;
    };

    const handleTableScroll = () => {
      if (syncingFromTop) return;
      syncingFromTable = true;
      topWrap.scrollLeft = tableWrap.scrollLeft;
      syncingFromTable = false;
    };

    syncTopMetrics();
    topWrap.addEventListener('scroll', handleTopScroll);
    tableWrap.addEventListener('scroll', handleTableScroll);
    window.addEventListener('resize', syncTopMetrics);

    return () => {
      topWrap.removeEventListener('scroll', handleTopScroll);
      tableWrap.removeEventListener('scroll', handleTableScroll);
      window.removeEventListener('resize', syncTopMetrics);
    };
  }, [bankProductMatrix.banks, bankProductMatrix.products, results]);

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, pt: 2, pb: 3 }}>
      <div className="loan-eligibility-shell">
        <div className="loan-eligibility-header">
          <PageHeader
            title="Loan Eligibility Calculator"
            subtitle={
              `${projectId ? `Active Project: ${projectId}` : 'No active project selected.'}` +
              `${lastRunAt ? `  •  Last computed: ${new Intl.DateTimeFormat(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(lastRunAt)}` : ''}`
            }
            actions={
              <Stack direction="row" spacing={1} alignItems="center">
                {projectId ? <Chip label={`Project #${projectId}`} size="small" variant="outlined" /> : null}
              </Stack>
            }
          />
        </div>

        <div className="loan-eligibility-grid"
        style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(360px, 1.95fr) minmax(560px, 2.25fr)',
            gap: '16px',
            alignItems: 'start',
          }}
          >
          {/* LEFT: Customer details */}
          <div className="loan-eligibility-panel" style={{ minWidth: 0 }}>
            <div className="panel-header">
              <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700 }}>
                Customer Details
              </Typography>
              <div className="loan-eligibility-actions">
                <button className="btn" type="button" onClick={calculateEmi}>
                  Calculate EMI
                </button>
                <button className="btn" type="button" onClick={runOptimization} disabled={loading}>
                  {loading ? 'Calculating...' : 'Check Eligibility'}
                </button>
                {emiResult !== null ? (
                  <span className="loan-eligibility-badge">
                    <strong>EMI:</strong> {formatINR2dp(emiResult)}
                  </span>
                ) : null}
              </div>
            </div>

            <div className="panel-body">

              {sttSupported ? (
                sttActiveField ? (
                  <div style={{ marginBottom: 10, fontSize: 13, opacity: 0.85 }}>
                    🎙️ Listening for <strong>{sttActiveField}</strong>
                    {sttInterim ? <span> — “{sttInterim}”</span> : null}
                    <span style={{ marginLeft: 10, opacity: 0.75 }}>
                      (Double Ctrl / Double Alt or Ctrl+Shift+S to toggle, Esc to stop)
                    </span>
                  </div>
                ) : null
              ) : (
                <div style={{ marginBottom: 10, fontSize: 13, opacity: 0.8 }}>
                  Speech-to-text is not supported in this browser.
                </div>
              )}

              {sttError ? (
                <div style={{ marginBottom: 10, fontSize: 13, color: '#b00020' }}>
                  STT error: {String(sttError)}
                </div>
              ) : null}

              {canUseSpeechToText ? (
                <div
                  style={{
                    marginBottom: 12,
                    padding: 10,
                    border: '1px solid rgba(0,0,0,0.12)',
                    borderRadius: 8,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 10,
                    alignItems: 'center'
                  }}
                >

                {smartVoiceEnabled ? (
                  <>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      if (smartVoiceListening) {
                        stopSmartVoiceCapture();
                        return;
                      }

                      smartVoiceShouldContinueRef.current = true;
                      setSttError(null);
                      setSmartVoiceTranscript('');
                      startSmartVoiceCapture();
                    }}
                    disabled={!sttSupported}
                  >
                    {smartVoiceListening ? 'Stop Smart Voice' : 'Start Smart Voice'}
                  </button>

                    <span style={{ fontSize: 13, opacity: 0.8 }}>
                      Example: “property value 6 crore”, “monthly income 5 lakh”, “average bank balance 2 lakh”
                    </span>

                    {smartVoiceTranscript ? (
                      <span style={{ fontSize: 13, opacity: 0.85 }}>
                        Heard: <strong>{smartVoiceTranscript}</strong>
                      </span>
                    ) : null}
                  </>
                ) : null}
                </div>
              ) : null}

              <div className="loan-eligibility-form-grid">

                <label>
                  {renderFieldLabel('Name')}
                  <div className="stt-field-row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input name="name" onFocus={handleFocus} data-stt-field="name" placeholder="Full name" value={form.name} onChange={updateForm} />
                    <button
                      type="button"
                      className={`stt-mic-btn ${!canUseSpeechToText ? 'stt-mic-hidden' : ''} ${sttActiveField === 'name' ? 'active' : ''}`}
                      title="Speak to fill (Ctrl Ctrl / Alt Alt / Ctrl+Shift+S)"
                      onClick={() => toggleSttForField('name')}
                      disabled={!sttSupported}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        border: '1px solid rgba(0,0,0,0.25)',
                        background: sttActiveField === 'name' ? 'rgba(0,0,0,0.08)' : 'transparent',
                        cursor: sttSupported ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {sttActiveField === 'name' ? '⏹' : '🎤'}
                    </button>
                  </div>
                </label>

                <label>
                  {renderFieldLabel('Age', 'Years')}
                  <div className="stt-field-row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input name="age" onFocus={handleFocus} data-stt-field="age" type="number" placeholder="Age" value={form.age} onChange={updateForm} />
                    <button
                      type="button"
                      className={`stt-mic-btn ${!canUseSpeechToText ? 'stt-mic-hidden' : ''} ${sttActiveField === 'age' ? 'active' : ''}`}
                      title="Speak to fill (Ctrl Ctrl / Alt Alt / Ctrl+Shift+S)"
                      onClick={() => toggleSttForField('age')}
                      disabled={!sttSupported}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        border: '1px solid rgba(0,0,0,0.25)',
                        background: sttActiveField === 'age' ? 'rgba(0,0,0,0.08)' : 'transparent',
                        cursor: sttSupported ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {sttActiveField === 'age' ? '⏹' : '🎤'}
                    </button>
                  </div>
                </label>

                <label>
                  {renderFieldLabel('Email')}
                  <div className="stt-field-row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input name="email" onFocus={handleFocus} data-stt-field="email" type="email" placeholder="Email" value={form.email} onChange={updateForm} />
                    <button
                      type="button"
                      className={`stt-mic-btn ${!canUseSpeechToText ? 'stt-mic-hidden' : ''} ${sttActiveField === 'email' ? 'active' : ''}`}
                      title="Speak to fill (Ctrl Ctrl / Alt Alt / Ctrl+Shift+S)"
                      onClick={() => toggleSttForField('email')}
                      disabled={!sttSupported}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        border: '1px solid rgba(0,0,0,0.25)',
                        background: sttActiveField === 'email' ? 'rgba(0,0,0,0.08)' : 'transparent',
                        cursor: sttSupported ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {sttActiveField === 'email' ? '⏹' : '🎤'}
                    </button>
                  </div>
                </label>

                <label>
                  {renderFieldLabel('Phone')}
                  <div className="stt-field-row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input name="phone" onFocus={handleFocus} data-stt-field="phone" type="tel" placeholder="Phone" value={form.phone} onChange={updateForm} />
                    <button
                      type="button"
                      className={`stt-mic-btn ${!canUseSpeechToText ? 'stt-mic-hidden' : ''} ${sttActiveField === 'phone' ? 'active' : ''}`}
                      title="Speak to fill (Ctrl Ctrl / Alt Alt / Ctrl+Shift+S)"
                      onClick={() => toggleSttForField('phone')}
                      disabled={!sttSupported}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        border: '1px solid rgba(0,0,0,0.25)',
                        background: sttActiveField === 'phone' ? 'rgba(0,0,0,0.08)' : 'transparent',
                        cursor: sttSupported ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {sttActiveField === 'phone' ? '⏹' : '🎤'}
                    </button>
                  </div>
                </label>

                <label className="loan-eligibility-occupation">
                  {renderFieldLabel('Occupation')}
                  <select
                    value={form.isSalaried ? 'Salaried' : 'Self Employed'}
                    onChange={(e) => setForm((f) => ({ ...f, isSalaried: e.target.value === 'Salaried' }))}
                  >
                    <option value="Salaried">Salaried</option>
                    <option value="Self Employed">Self Employed</option>
                  </select>
                </label>

                {form.isSalaried ? (
                  <label>
                    {renderFieldLabel('Employer')}
                    <select name="employerType" value={form.employerType} onChange={updateForm}>
                      <option value="Public Ltd.">Public Ltd.</option>
                      <option value="Pvt. Ltd.">Pvt. Ltd.</option>
                      <option value="LLP">LLP</option>
                      <option value="Partnership">Partnership</option>
                      <option value="Proprietorship">Proprietorship</option>
                      <option value="Government">Government</option>
                    </select>
                  </label>
                ) : null}

                <label>
                  {renderFieldLabel('Monthly Income', 'INR / month')}
                  <div className="stt-field-row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      name="monthlyIncome" onFocus={handleFocus} data-stt-field="monthlyIncome"
                      type="text"
                      inputMode="decimal"
                      placeholder="e.g. 50000 (press L / C)"
                      value={form.monthlyIncome}
                      onChange={updateForm}
                      onKeyDown={(e) =>
                        handleMoneyShortcutKeyDown(e, form.monthlyIncome, (v) => setMoneyField('monthlyIncome', v))
                      }

                      onBlur={() => formatMoneyOnBlur('monthlyIncome', { decimals: 0 })} />
                    <button
                      type="button"
                      className={`stt-mic-btn ${!canUseSpeechToText ? 'stt-mic-hidden' : ''} ${sttActiveField === 'monthlyIncome' ? 'active' : ''}`}
                      title="Speak to fill (Ctrl Ctrl / Alt Alt / Ctrl+Shift+S)"
                      onClick={() => toggleSttForField('monthlyIncome')}
                      disabled={!sttSupported}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        border: '1px solid rgba(0,0,0,0.25)',
                        background: sttActiveField === 'monthlyIncome' ? 'rgba(0,0,0,0.08)' : 'transparent',
                        cursor: sttSupported ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {sttActiveField === 'monthlyIncome' ? '⏹' : '🎤'}
                    </button>
                  </div>
                </label>

                <label>
                  {renderFieldLabel('Existing Obligations', 'INR / month')}
                  <div className="stt-field-row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      name="existingObligations" onFocus={handleFocus} data-stt-field="existingObligations"
                      type="text"
                      inputMode="decimal"
                      placeholder="e.g. 10000 (press L / C)"
                      value={form.existingObligations}
                      onChange={updateForm}
                      onKeyDown={(e) =>
                        handleMoneyShortcutKeyDown(e, form.existingObligations, (v) =>
                          setMoneyField('existingObligations', v)
                        )
                      }

                      onBlur={() => formatMoneyOnBlur('existingObligations', { decimals: 0 })} />
                    <button
                      type="button"
                      className={`stt-mic-btn ${!canUseSpeechToText ? 'stt-mic-hidden' : ''} ${sttActiveField === 'existingObligations' ? 'active' : ''}`}
                      title="Speak to fill (Ctrl Ctrl / Alt Alt / Ctrl+Shift+S)"
                      onClick={() => toggleSttForField('existingObligations')}
                      disabled={!sttSupported}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        border: '1px solid rgba(0,0,0,0.25)',
                        background: sttActiveField === 'existingObligations' ? 'rgba(0,0,0,0.08)' : 'transparent',
                        cursor: sttSupported ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {sttActiveField === 'existingObligations' ? '⏹' : '🎤'}
                    </button>
                  </div>
                </label>

                <label>
                  {renderFieldLabel('Property Value', 'INR')}
                  <div className="stt-field-row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      name="propertyValue" onFocus={handleFocus} data-stt-field="propertyValue"
                      type="text"
                      inputMode="decimal"
                      placeholder="e.g. 5000000 (press L / C)"
                      value={form.propertyValue}
                      onChange={updateForm}
                      onKeyDown={(e) =>
                        handleMoneyShortcutKeyDown(e, form.propertyValue, (v) => setMoneyField('propertyValue', v))
                      }

                      onBlur={() => formatMoneyOnBlur('propertyValue', { decimals: 0 })} />
                    <button
                      type="button"
                      className={`stt-mic-btn ${!canUseSpeechToText ? 'stt-mic-hidden' : ''} ${sttActiveField === 'propertyValue' ? 'active' : ''}`}
                      title="Speak to fill (Ctrl Ctrl / Alt Alt / Ctrl+Shift+S)"
                      onClick={() => toggleSttForField('propertyValue')}
                      disabled={!sttSupported}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        border: '1px solid rgba(0,0,0,0.25)',
                        background: sttActiveField === 'propertyValue' ? 'rgba(0,0,0,0.08)' : 'transparent',
                        cursor: sttSupported ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {sttActiveField === 'propertyValue' ? '⏹' : '🎤'}
                    </button>
                  </div>
                </label>


                <label>
                  {renderFieldLabel('Desired Loan', 'INR')}
                  <div className="stt-field-row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      name="desiredLoan" onFocus={handleFocus} data-stt-field="desiredLoan"
                      type="text"
                      inputMode="decimal"
                      placeholder="e.g. 2500000 (press L / C)"
                      value={form.desiredLoan}
                      onChange={updateForm}
                      onKeyDown={(e) =>
                        handleMoneyShortcutKeyDown(e, form.desiredLoan, (v) => setMoneyField('desiredLoan', v))
                      }

                      onBlur={() => formatMoneyOnBlur('desiredLoan', { decimals: 0 })} />
                    <button
                      type="button"
                      className={`stt-mic-btn ${!canUseSpeechToText ? 'stt-mic-hidden' : ''} ${sttActiveField === 'desiredLoan' ? 'active' : ''}`}
                      title="Speak to fill (Ctrl Ctrl / Alt Alt / Ctrl+Shift+S)"
                      onClick={() => toggleSttForField('desiredLoan')}
                      disabled={!sttSupported}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        border: '1px solid rgba(0,0,0,0.25)',
                        background: sttActiveField === 'desiredLoan' ? 'rgba(0,0,0,0.08)' : 'transparent',
                        cursor: sttSupported ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {sttActiveField === 'desiredLoan' ? '⏹' : '🎤'}
                    </button>
                  </div>
                </label>

                <label>
                  {renderFieldLabel('ABB', 'INR / month')}
                  <div className="stt-field-row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      name="averageMonthlyBalance" onFocus={handleFocus} data-stt-field="averageMonthlyBalance"
                      type="text"
                      inputMode="decimal"
                      placeholder="e.g. 150000 (press L / C)"
                      value={form.averageMonthlyBalance}
                      onChange={updateForm}
                      onKeyDown={(e) =>
                        handleMoneyShortcutKeyDown(e, form.averageMonthlyBalance, (v) =>
                          setMoneyField('averageMonthlyBalance', v)
                        )
                      }

                      onBlur={() => formatMoneyOnBlur('averageMonthlyBalance', { decimals: 0 })} />
                    <button
                      type="button"
                      className={`stt-mic-btn ${!canUseSpeechToText ? 'stt-mic-hidden' : ''} ${sttActiveField === 'averageMonthlyBalance' ? 'active' : ''}`}
                      title="Speak to fill (Ctrl Ctrl / Alt Alt / Ctrl+Shift+S)"
                      onClick={() => toggleSttForField('averageMonthlyBalance')}
                      disabled={!sttSupported}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        border: '1px solid rgba(0,0,0,0.25)',
                        background: sttActiveField === 'averageMonthlyBalance' ? 'rgba(0,0,0,0.08)' : 'transparent',
                        cursor: sttSupported ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {sttActiveField === 'averageMonthlyBalance' ? '⏹' : '🎤'}
                    </button>
                  </div>
                </label>

                {!form.isSalaried ? (
                  <label>
                    {renderFieldLabel('Yearly Turnover', 'INR / year')}
                    <div className="stt-field-row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        name="yearlyTurnover" onFocus={handleFocus} data-stt-field="yearlyTurnover"
                        type="text"
                        inputMode="decimal"
                        placeholder="e.g. 10000000 (press L / C)"
                        value={form.yearlyTurnover}
                        onChange={updateForm}
                        onKeyDown={(e) =>
                          handleMoneyShortcutKeyDown(e, form.yearlyTurnover, (v) => setMoneyField('yearlyTurnover', v))
                        }

                        onBlur={() => formatMoneyOnBlur('yearlyTurnover', { decimals: 0 })} />
                      <button
                        type="button"
                        className={`stt-mic-btn ${!canUseSpeechToText ? 'stt-mic-hidden' : ''} ${sttActiveField === 'yearlyTurnover' ? 'active' : ''}`}
                        title="Speak to fill (Ctrl Ctrl / Alt Alt / Ctrl+Shift+S)"
                        onClick={() => toggleSttForField('yearlyTurnover')}
                        disabled={!sttSupported}
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 8,
                          border: '1px solid rgba(0,0,0,0.25)',
                          background: sttActiveField === 'yearlyTurnover' ? 'rgba(0,0,0,0.08)' : 'transparent',
                          cursor: sttSupported ? 'pointer' : 'not-allowed',
                        }}
                      >
                        {sttActiveField === 'yearlyTurnover' ? '⏹' : '🎤'}
                      </button>
                    </div>
                  </label>
                ) : null}

                {!form.isSalaried ? (
                  <label>
                    {renderFieldLabel('Sector')}
                    <select name="sector" value={form.sector} onChange={updateForm}>
                      <option value="Trading">Trading</option>
                      <option value="Manufacturing">Manufacturing</option>
                      <option value="Service">Service</option>
                      <option value="Other">Other</option>
                    </select>
                  </label>
                ) : null}

                <label>
                  {renderFieldLabel('Tenure', 'Months')}
                  <div className="stt-field-row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      name="tenureMonths" onFocus={handleFocus} data-stt-field="tenureMonths"
                      type="number"
                      placeholder="e.g. 240"
                      value={form.tenureMonths}
                      onChange={updateForm}
                    />
                    <button
                      type="button"
                      className={`stt-mic-btn ${!canUseSpeechToText ? 'stt-mic-hidden' : ''} ${sttActiveField === 'tenureMonths' ? 'active' : ''}`}
                      title="Speak to fill (Ctrl Ctrl / Alt Alt / Ctrl+Shift+S)"
                      onClick={() => toggleSttForField('tenureMonths')}
                      disabled={!sttSupported}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        border: '1px solid rgba(0,0,0,0.25)',
                        background: sttActiveField === 'tenureMonths' ? 'rgba(0,0,0,0.08)' : 'transparent',
                        cursor: sttSupported ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {sttActiveField === 'tenureMonths' ? '⏹' : '🎤'}
                    </button>
                  </div>
                  {suggestedTenure ? (
                    <div
                      className="loan-eligibility-suggested-tenure"
                      style={{ fontWeight: 800, marginTop: 6, fontSize: '0.85rem' }}
                    >
                      Suggested Tenure (max permissible): {suggestedTenure.maxAge} − {suggestedTenure.ageNum} = {suggestedTenure.years} years ({suggestedTenure.months} months)
                    </div>
                  ) : null}
                </label>

                <label>
                  {renderFieldLabel('Interest Rate', '% p.a.')}
                  <div className="stt-field-row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      name="annualInterestRatePct" onFocus={handleFocus} data-stt-field="annualInterestRatePct"
                      type="number"
                      step="0.1"
                      placeholder="e.g. 9.5"
                      value={form.annualInterestRatePct}
                      onChange={updateForm}
                    />
                    <button
                      type="button"
                      className={`stt-mic-btn ${!canUseSpeechToText ? 'stt-mic-hidden' : ''} ${sttActiveField === 'annualInterestRatePct' ? 'active' : ''}`}
                      title="Speak to fill (Ctrl Ctrl / Alt Alt / Ctrl+Shift+S)"
                      onClick={() => toggleSttForField('annualInterestRatePct')}
                      disabled={!sttSupported}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        border: '1px solid rgba(0,0,0,0.25)',
                        background: sttActiveField === 'annualInterestRatePct' ? 'rgba(0,0,0,0.08)' : 'transparent',
                        cursor: sttSupported ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {sttActiveField === 'annualInterestRatePct' ? '⏹' : '🎤'}
                    </button>
                  </div>
                </label>

                <label>
                  {renderFieldLabel('EMI', 'INR / month')}
                  <div className="stt-field-row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      name="newObligation" onFocus={handleFocus} data-stt-field="newObligation"
                      type="text"
                      inputMode="decimal"
                      placeholder="Calculated EMI / manual (press L / C)"
                      value={form.newObligation}
                      onChange={updateForm}
                      onKeyDown={(e) =>
                        handleMoneyShortcutKeyDown(e, form.newObligation, (v) => setMoneyField('newObligation', v))
                      }

                      onBlur={() => formatMoneyOnBlur('newObligation', { decimals: 2 })} />
                    <button
                      type="button"
                      className={`stt-mic-btn ${!canUseSpeechToText ? 'stt-mic-hidden' : ''} ${sttActiveField === 'newObligation' ? 'active' : ''}`}
                      title="Speak to fill (Ctrl Ctrl / Alt Alt / Ctrl+Shift+S)"
                      onClick={() => toggleSttForField('newObligation')}
                      disabled={!sttSupported}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        border: '1px solid rgba(0,0,0,0.25)',
                        background: sttActiveField === 'newObligation' ? 'rgba(0,0,0,0.08)' : 'transparent',
                        cursor: sttSupported ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {sttActiveField === 'newObligation' ? '⏹' : '🎤'}
                    </button>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* RIGHT: Results */}
          <div className="loan-eligibility-panel"  style={{ minWidth: 0 }}>
            <div className="panel-header">
              <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700 }}>
                Results
              </Typography>
              {bestOverall ? (
                <Chip
                  size="small"
                  variant="outlined"
                  title="Best overall option"
                  label={`${bestOverall.bankName} · ${getDisplayProductName(bestOverall.product)}`}
                />
              ) : (
                <Chip size="small" variant="outlined" label="Run “Check Eligibility”" />
              )}
            </div>

            <div className="loan-eligibility-right-stack">
              <div className="panel-body" style={{ paddingBottom: '0.65em' }}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Status: ${bestOverall ? 'Computed' : 'Waiting'}`}
                    color={bestOverall ? 'success' : 'default'}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Best Allowable Loan: ${bestOverall ? formatINR2dp(bestOverall.loanAmount || 0) : '—'}`}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Optimizer: ${bestOverall ? (bestOverall.optimizer || '—') : '—'}`}
                  />
                  <Chip size="small" variant="outlined" color="success" label={`Eligible: ${approvedCount}`} />
                  <Chip size="small" variant="outlined" color="error" label={`Not Eligible: ${rejectedCount}`} />
                </Paper>
              </div>
              <Divider sx={{ my: 1.5 }} />

              <Paper
                variant="outlined"
                sx={{
                  p: 1.1,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Chip
                  size="small"
                  variant="outlined"
                  color={selectedOffer ? 'primary' : 'default'}
                  label={
                    selectedOffer
                      ? `Selected: ${selectedOffer.bankName} · ${getDisplayProductName(selectedOffer.product)}`
                      : 'Select any bank/product cell to generate PDF'
                  }
                />
                <button
                  className="btn"
                  type="button"
                  onClick={() => generateOfferPdf(selectedOffer)}
                  disabled={!selectedOffer}
                >
                  Generate PDF
                </button>
              </Paper>

              <Divider sx={{ my: 1.5 }} />

              <div
                className="loan-eligibility-table-top-scroll"
                ref={resultTableTopScrollRef}
                aria-hidden="true"
                style={{
                  display: 'none',
                  width: '100%',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  height: 14,
                  borderRadius: 999,
                  border: '1px solid rgba(148, 163, 184, 0.35)',
                  background: 'rgba(15, 23, 42, 0.18)',
                  marginBottom: 10,
                }}
              >
                <div style={{ height: 1 }} />
              </div>

              <div
                className="loan-eligibility-table-wrap"
                ref={resultTableScrollRef}
                style={{ width: '100%', overflowX: 'auto', overflowY: 'hidden' }}
              >
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: 2,
                    overflow: 'visible',
                    minWidth: `${tableMinWidth}px`,
                  }}
                >
                  <Table
                    size="small"
                    aria-label="loan offers matrix"
                    sx={{
                      minWidth: `${tableMinWidth}px`,
                      tableLayout: 'fixed',
                      '& td, & th': { verticalAlign: 'top' },
                    }}
                  >
                    <TableHead>
                      <TableRow
                        sx={{
                          bgcolor: 'action.hover',
                          '& th': { fontWeight: 900, color: 'text.secondary' },
                        }}
                      >
                        <TableCell sx={{ width: 180, minWidth: 180 }}>Bank</TableCell>
                        {bankProductMatrix.products.map((p) => (
                          <TableCell
                            key={p}
                            align="left"
                            sx={{ width: 280, minWidth: 280, maxWidth: 280 }}
                          >
                            {p}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {bankProductMatrix.banks.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={1 + bankProductMatrix.products.length}
                            sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}
                          >
                            No results yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        bankProductMatrix.banks.map((bank) => (
                          <TableRow key={bank} hover sx={{ '& td': { borderColor: 'divider' } }}>
                            <TableCell sx={{ fontWeight: 800, width: 180, minWidth: 180 }}>{bank}</TableCell>

                            {bankProductMatrix.products.map((p) => {
                              const resultEntry = bankProductMatrix.matrix?.[bank]?.[p];
                              const amount = Number(resultEntry?.loanAmount || 0);
                              const secondaryText = getResultSecondaryText(resultEntry);
                              const decisionText = getResultDecisionText(resultEntry);
                              const isApproved = !!resultEntry?.isApproved;
                              const isSelected =
                                !!resultEntry &&
                                String(selectedOffer?.bankName || '') === String(resultEntry?.bankName || '') &&
                                String(getDisplayProductName(selectedOffer?.product || '')) === String(getDisplayProductName(resultEntry?.product || ''));

                              if (resultEntry) {
                                return (
                                  <TableCell
                                    key={p}
                                    align="left"
                                    onClick={() => setSelectedOffer(resultEntry)}
                                    sx={{
                                      width: 280,
                                      minWidth: 280,
                                      maxWidth: 280,
                                      cursor: 'pointer',
                                      fontVariantNumeric: 'tabular-nums',
                                      bgcolor: isSelected ? 'action.selected' : 'transparent',
                                      outline: isSelected ? '2px solid rgba(37, 99, 235, 0.55)' : 'none',
                                      outlineOffset: '-2px',
                                    }}
                                  >
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                                        <div style={{ fontWeight: 700 }}>{amount > 0 ? formatINR2dp(amount) : '—'}</div>
                                        <Chip
                                          label={isApproved ? 'Eligible' : 'Not Eligible'}
                                          size="small"
                                          color={isApproved ? 'success' : 'error'}
                                          variant="outlined"
                                        />
                                      </div>

                                      {secondaryText ? (
                                        <div style={{ fontSize: '0.72rem', opacity: 0.8, lineHeight: 1.35, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                          {secondaryText}
                                        </div>
                                      ) : null}

                                      {decisionText ? (
                                        <div style={{ fontSize: '0.72rem', opacity: 0.9, lineHeight: 1.35, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                          {decisionText}
                                        </div>
                                      ) : null}
                                    </div>
                                  </TableCell>
                                );
                              }

                              return (
                                <TableCell key={p} align="left" sx={{ width: 280, minWidth: 280, maxWidth: 280 }}>
                                  <Chip label="NA" size="small" variant="outlined" />
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>

              <Divider sx={{ my: 1.5 }} />


              <Paper
                variant="outlined"
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Input checks
                </Typography>
                <Stack spacing={0.75}>
                  {inputChecks.length ? inputChecks.map((item, index) => (
                    <Chip
                      key={`${item.text}-${index}`}
                      size="small"
                      variant="outlined"
                      color={item.severity === 'warning' ? 'warning' : 'default'}
                      label={item.text}
                      sx={{ justifyContent: 'flex-start', '& .MuiChip-label': { display: 'block', whiteSpace: 'normal' } }}
                    />
                  )) : (
                    <Typography variant="body2" color="text.secondary">
                      No input issues detected from the current values.
                    </Typography>
                  )}
                </Stack>
              </Paper>

              <Divider sx={{ my: 1.5 }} />

              <div className="loan-eligibility-chart">
                <div style={{ marginBottom: '0.3em', fontWeight: 600, color: 'var(--heading-color)' }}>
                  Comparison by Bank
                </div>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={results?.bestPerBank || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bankName" />
                    <YAxis tickFormatter={(v) => formatINR0dp(v)} />
                    <Tooltip formatter={(value) => formatINR2dp(value)} />
                    <Legend />
                    <Bar dataKey="loanAmount" fill="#2575fc" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Box>
  );
}

